// src/pages/SiteMap.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  Link,
  IconButton,
} from "@mui/material";

import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import LaunchRounded from "@mui/icons-material/Launch";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";

// ✅ B2B-only now
import navConfigB2B from "../../NavConfigB2B";
import { getPrimaryColor } from "../../GlobalFunction/Theme";
import { getLangFromCookie, LANG_COOKIE } from "../../../Utils/LangCookie";

/* ----------------- shared helpers ----------------- */
const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

const isExternalHref = (href = "") => /^https?:\/\//i.test(String(href || ""));
const mapInternalTo = (navHref = "") =>
  String(navHref || "").startsWith("/")
    ? String(navHref || "")
    : `/${String(navHref || "").replace(/^\/+/, "")}`;

/** ✅ Remove /b2b prefix from any internal path */
const stripB2BPrefix = (p = "") =>
  String(p || "")
    .replace(/^\/b2b(\/|$)/, "/")
    .replace(/\/{2,}/g, "/");

/** ✅ If your old configs used "b2b/xxx" without leading slash */
const normalizeInternalPath = (p = "") => stripB2BPrefix(mapInternalTo(p));

const getTitle = (node, lang = "ZH") => {
  if (!node) return "";
  if (lang === "EN" && node.EN) return node.EN;
  if (lang === "JP" && node.JP) return node.JP;
  return node.label ?? node.EN ?? node.JP ?? "";
};

/** B2B-only config */
const pickConfig = () => navConfigB2B;

/** Navbar-consistent category linker (B2B-only; no /b2b prefix) */
function toForCategory(href = "") {
  const productInfoBase = "/商品資訊";
  if (!href) return productInfoBase;

  // In old config, href might be "/b2b/商品資訊/xxx" -> strip it
  const cleanHref = stripB2BPrefix(href);

  const search = `?cat=${encodeURIComponent(cleanHref)}`;
  return `${productInfoBase}${search}`;
}

/**
 * ✅ Route logic aligned with Navbar.jsx (MobileTree + top-level special cases)
 * B2B-only now:
 * - remove modePrefix
 * - remove /b2b from all returns
 * Returns: { kind: "external" | "internal", to/href, state }
 */
function resolveNavLink({ node, ancestors = [] }) {
  const rawHref = node?.href || "";
  const labelText = String(node?.title || node?.label || "");
  const externalFlag = !!node?.external;

  const baseLabel = "商品資訊";
  const productInfoBase = "/商品資訊";

  // external by flag or url
  if (externalFlag || isExternalHref(rawHref)) {
    const href = rawHref || "#";
    return { kind: "external", href };
  }

  // QA special (B2B)
  const isQAPage =
    labelText.includes("常見問題") || labelText.includes("常見Q&A");

  // product tree (category routing)
  const underProductTree =
    ancestors.includes(baseLabel) || ancestors[0] === baseLabel;

  // ✅ B2B philosophy special: "/企業理念" should go to "/公司簡介" with tab=1
  const isB2BPhilosophy =
    (rawHref &&
      (stripB2BPrefix(rawHref) === "/企業理念" ||
        stripB2BPrefix(rawHref) === "企業理念")) ||
    labelText.includes("企業理念");

  // ✅ B2B consulting special (treat like category mega)
  const isB2BConsulting =
    labelText.includes("專業諮詢") ||
    labelText.includes("Consultation") ||
    labelText.includes("相談") ||
    String(rawHref).includes("專業諮詢") ||
    String(rawHref).includes("專人服務");

  // normalize both () and （） variants (no /b2b now)
  const consultingHrefCandidates = [
    "/商品資訊/專業諮詢(專人服務)",
    "/商品資訊/專業諮詢（專人服務）",
    "/b2b/商品資訊/專業諮詢(專人服務)",
    "/b2b/商品資訊/專業諮詢（專人服務）",
  ];
  const consultingHrefRaw =
    consultingHrefCandidates.find((x) => String(rawHref) === x) ||
    consultingHrefCandidates[1];
  const consultingHref = stripB2BPrefix(consultingHrefRaw);

  // root product label (if no href)
  const isRootProductLabel = labelText.includes(baseLabel) && !rawHref;

  // "商品資訊" root should be "fresh entry"
  const isProductInfoParent =
    labelText.includes("商品資訊") && !!node?.children?.length;

  if (isB2BPhilosophy) {
    return {
      kind: "internal",
      to: "/公司簡介",
      state: { tab: 1 },
    };
  }

  if (isB2BConsulting) {
    return {
      kind: "internal",
      to: toForCategory(consultingHref),
      state: { categoryHref: consultingHref },
    };
  }

  if (isQAPage) {
    return { kind: "internal", to: `/常見問題Q&A` };
  }

  if (underProductTree || isRootProductLabel) {
    const catHref = stripB2BPrefix(rawHref || "");
    return {
      kind: "internal",
      to: catHref ? toForCategory(catHref) : productInfoBase,
      state: catHref ? { categoryHref: catHref } : undefined,
    };
  }

  // normal internal
  return {
    kind: "internal",
    to: normalizeInternalPath(rawHref || "#"),
    state: isProductInfoParent
      ? { freshEntry: true, _ts: Date.now() }
      : undefined,
  };
}

function NavSmartLink({
  node,
  ancestors = [],
  underline = "hover",
  sx,
  children,
  ...rest
}) {
  const resolved = resolveNavLink({ node, ancestors });

  if (resolved.kind === "external") {
    return (
      <Link
        color="inherit"
        component="a"
        href={resolved.href}
        target="_blank"
        rel="noopener noreferrer"
        underline={underline}
        sx={sx}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      color="inherit"
      component={RouterLink}
      to={resolved.to}
      state={resolved.state}
      underline={underline}
      sx={sx}
      {...rest}
    >
      {children}
    </Link>
  );
}

/* =========================================================
   DESIGN 1 (Lion-style, 1st & 2nd level)
   ========================================================= */
function DesignLion({ lang }) {
  const cfg = pickConfig();
  const primary = getPrimaryColor("b2b");

  const sections = useMemo(() => {
    return (cfg || [])
      .map((p) => ({
        title: getTitle(p, lang),
        href: p.href || "",
        external: !!p.external,
        rawNode: p,
        items: Array.isArray(p.children)
          ? p.children.map((c) => ({
              title: getTitle(c, lang),
              href: c.href || "",
              external: !!c.external,
              rawNode: c,
            }))
          : [],
      }))
      .filter((s) => s.title);
  }, [cfg, lang]);

  const Item = ({ title, href, external, ancestors }) => (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: 32 }}
    >
      {external || isExternalHref(href) ? (
        <LaunchRounded sx={{ fontSize: 18, color: primary }} />
      ) : (
        <ChevronRightRounded sx={{ fontSize: 20, color: primary }} />
      )}

      <NavSmartLink
        node={{ title, label: title, href, external }}
        ancestors={ancestors}
        underline="hover"
        sx={{ fontSize: 16, fontFamily: fonts[lang] }}
      >
        {title}
      </NavSmartLink>
    </Box>
  );

  return (
    <Box>
      {sections.map((sec, i) => (
        <Box key={`${sec.title}-${i}`} sx={{ mb: { xs: 5, md: 8 } }}>
          <Box
            sx={{
              display: "block",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover .lionCircle": {
                backgroundColor: primary,
                borderColor: primary,
              },
              "&:hover .lionCircleIcon": { color: "#fff" },
              "&:hover .lionUnderline": {
                borderBottomColor: primary,
                borderBottomWidth: 3,
              },
            }}
          >
            {/* Header row (clickable) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                className="lionCircle"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: `2px solid ${primary}`,
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                  transition: "all 160ms ease",
                }}
              >
                <ChevronRightRounded
                  className="lionCircleIcon"
                  sx={{ fontSize: 20, color: primary }}
                />
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: "text.primary",
                  fontFamily: fonts[lang],
                }}
              >
                <NavSmartLink
                  node={{
                    title: sec.title,
                    label: sec.title,
                    href: sec.href,
                    external: sec.external,
                    children: sec.rawNode?.children,
                  }}
                  ancestors={[sec.title]}
                  underline="none"
                  sx={{ color: "inherit" }}
                >
                  {sec.title}
                </NavSmartLink>
              </Typography>
            </Box>

            <Box
              className="lionUnderline"
              sx={{
                mt: 1.25,
                borderBottom: `2px solid ${primary}`,
                width: "100%",
                transition:
                  "border-color 160ms ease, border-bottom-width 160ms ease",
              }}
            />
          </Box>

          <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
            {(sec.items || []).map((it, idx) => (
              <Grid item xs={12} sm={6} md={3} key={`${it.title}-${idx}`}>
                <Item
                  title={it.title}
                  href={it.href}
                  external={it.external}
                  ancestors={[sec.title]}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

/* =========================================================
   DESIGN 3 (Tree-style sitemap from navConfig)
   - uses same routing logic as Navbar
   ========================================================= */
const COLLAPSE_SIBLINGS = true;

function toTree(node, lang = "ZH") {
  if (!node) return null;
  const title = getTitle(node, lang);
  if (!title) return null;

  const children = Array.isArray(node.children)
    ? node.children.map((c) => toTree(c, lang)).filter(Boolean)
    : [];

  return {
    title,
    label: node.label ?? title,
    href: node.href || "",
    external: !!node.external,
    hasChildren: !!node.children?.length,
    children,
  };
}

function RouteLink({ node, ancestors = [], lang, sx }) {
  return (
    <NavSmartLink
      node={{
        title: node.title,
        label: node.label,
        href: node.href,
        external: node.external,
        children: node.hasChildren ? [{}] : undefined,
      }}
      ancestors={ancestors}
      underline="hover"
      sx={{ fontFamily: fonts[lang], ...sx }}
    >
      {node.title}
    </NavSmartLink>
  );
}

/* ---------- Chevron row ---------- */
function ChevronRow({
  node,
  ancestors = [],
  lang,
  isOpen,
  canExpand,
  onToggle,
  sx,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 28,
        cursor: canExpand ? "pointer" : "default",
        ...sx,
      }}
      onClick={canExpand ? onToggle : undefined}
    >
      <ChevronRightRounded
        sx={{
          fontSize: 18,
          color: "text.secondary",
          transform: canExpand && isOpen ? "rotate(90deg)" : "none",
          transition: "transform .15s ease",
        }}
      />
      <RouteLink
        node={node}
        ancestors={ancestors}
        lang={lang}
        sx={{ fontSize: 15, color: "text.primary" }}
      />
    </Box>
  );
}

/* ---------- Recursive child list with sibling-collapse ---------- */
function ChildrenList({
  items = [],
  ancestors = [],
  lang,
  pathKey,
  openMap,
  setOpenMap,
  indent = 0,
}) {
  if (!items.length) return null;

  return (
    <List
      disablePadding
      sx={{
        pl: indent ? 3 : 0,
        "& .MuiListItem-root": { px: 0, py: 0.25 },
      }}
    >
      {items.map((n, idx) => {
        const id = `${pathKey}/${idx}`;
        const hasChildren = !!n.children?.length;
        const isOpen = !!openMap[id];

        const handleToggle = () => {
          setOpenMap((prev) => {
            const next = { ...prev };
            const willOpen = !prev[id];
            next[id] = willOpen;

            if (COLLAPSE_SIBLINGS && willOpen) {
              const segs = id.split("/");
              const depth = segs.length;
              const parentKey = segs.slice(0, -1).join("/");

              Object.keys(next).forEach((key) => {
                if (key === id) return;
                const kSegs = key.split("/");
                const kDepth = kSegs.length;
                const kParent = kSegs.slice(0, -1).join("/");
                if (kDepth === depth && kParent === parentKey)
                  next[key] = false;
              });
              next[id] = true;
            }

            return next;
          });
        };

        return (
          <ListItem key={id} sx={{ display: "block" }}>
            <ChevronRow
              node={n}
              ancestors={ancestors}
              lang={lang}
              isOpen={isOpen}
              canExpand={hasChildren}
              onToggle={handleToggle}
            />

            {hasChildren && isOpen && (
              <ChildrenList
                items={n.children}
                ancestors={ancestors}
                lang={lang}
                pathKey={id}
                openMap={openMap}
                setOpenMap={setOpenMap}
                indent={Math.min(indent + 1, 2)}
              />
            )}
          </ListItem>
        );
      })}
    </List>
  );
}

function buildTreeSections({ lang }) {
  const src = pickConfig() || [];
  const sections = src.map((n) => toTree(n, lang)).filter(Boolean);
  return { title: "網站地圖", sections };
}

export function DesignTree({ lang }) {
  const { sections } = useMemo(() => buildTreeSections({ lang }), [lang]);
  const primary = getPrimaryColor("b2b");

  const [topOpen, setTopOpen] = useState(() =>
    sections.reduce((acc, _, i) => {
      acc[i] = true;
      return acc;
    }, {}),
  );
  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    setTopOpen(
      sections.reduce((acc, _, i) => {
        acc[i] = true;
        return acc;
      }, {}),
    );

    setOpenMap({});
  }, [sections]);

  return (
    <Box sx={{ mt: 1 }}>
      {sections.map((sec, i) => {
        const isOpen = !!topOpen[i];
        const topKey = `${i}`;

        return (
          <Box key={`${sec.title}-${i}`} sx={{ mb: { xs: 3.5, md: 5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                cursor: "pointer",
                py: 1,
              }}
              onClick={() => setTopOpen((prev) => ({ ...prev, [i]: !prev[i] }))}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  flex: 1,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: `2px solid ${primary}`,
                    display: "grid",
                    placeItems: "center",
                    color: primary,
                    flex: "0 0 auto",
                  }}
                >
                  <ChevronRightRounded sx={{ fontSize: 18 }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: "text.primary",
                    fontFamily: fonts[lang],
                    letterSpacing: 0.2,
                  }}
                >
                  <NavSmartLink
                    node={{
                      title: sec.title,
                      label: sec.label,
                      href: sec.href,
                      external: sec.external,
                      children: sec.hasChildren ? [{}] : undefined,
                    }}
                    ancestors={[sec.title]}
                    underline="none"
                    sx={{ color: "inherit" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {sec.title}
                  </NavSmartLink>
                </Typography>
              </Box>

              <IconButton
                aria-label={isOpen ? "收合" : "展開"}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setTopOpen((prev) => ({ ...prev, [i]: !prev[i] }));
                }}
              >
                {isOpen ? (
                  <RemoveRounded sx={{ color: "text.secondary" }} />
                ) : (
                  <AddRounded sx={{ color: "text.secondary" }} />
                )}
              </IconButton>
            </Box>

            <Box
              sx={{ mt: 1, borderBottom: "1px solid", borderColor: "divider" }}
            />

            {isOpen && (
              <Box sx={{ mt: 1.5 }}>
                <ChildrenList
                  items={sec.children}
                  ancestors={[sec.title]}
                  lang={lang}
                  pathKey={topKey}
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                  indent={0}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/* =========================================================
   B2B-only SiteMap
   ========================================================= */
export default function SiteMap({ pageTitle }) {
  // 👇 language state reacts to navbar changes
  const getCurrentLang = () =>
    normalizeLang(
      getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH",
    );
  const [lang, setLang] = useState(getCurrentLang);

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  return (
    <Box
      component="main"
      sx={{
        py: { xs: 4, md: 6 },
        minHeight: "100vh",
        backgroundColor: "#fffffc",
      }}
    >
      <Container maxWidth="lg">
        {/* Current default */}
        <DesignLion lang={lang} />
      </Container>
    </Box>
  );
}
